import { logger } from "../logger-init";
import { appInsightsService } from "./appInsights.service";

class GlobalErrorHandlerService {
  private isInitialized = false;

  initialize(): void {
    if (this.isInitialized) return;

    this.setupWindowErrorHandler();
    this.setupUnhandledRejectionHandler();
    this.setupConsoleErrorInterceptor();

    this.isInitialized = true;
    logger.info("Global error handlers initialized");
  }

  private setupWindowErrorHandler(): void {
    window.addEventListener("error", (event: ErrorEvent) => {
      const error = event.error || new Error(event.message);

      logger.error("Unhandled window error", error, {
        component: "GlobalErrorHandler",
        errorType: "windowError",
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        message: event.message,
      });

      appInsightsService.trackException(error, {
        source: "window.onerror",
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      });
    });
  }

  private setupUnhandledRejectionHandler(): void {
    window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
      const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason));

      logger.error("Unhandled promise rejection", error, {
        component: "GlobalErrorHandler",
        errorType: "unhandledRejection",
        reason: String(event.reason),
      });

      appInsightsService.trackException(error, {
        source: "unhandledrejection",
        reason: String(event.reason),
      });
    });
  }

  private setupConsoleErrorInterceptor(): void {
    const originalConsoleError = console.error;

    console.error = (...args: unknown[]) => {
      originalConsoleError.apply(console, args);

      try {
        const message = args
          .map((arg) => {
            if (typeof arg === "string") return arg;
            if (arg instanceof Error) return `${arg.name}: ${arg.message}`;
            try {
              return JSON.stringify(arg);
            } catch {
              return String(arg);
            }
          })
          .join(" ");

        if (!message.includes("[GlobalErrorHandler]")) {
          appInsightsService.trackEvent("ConsoleError", {
            message: message.substring(0, 1000),
            argCount: args.length,
          });
        }
      } catch {
        // Silently fail telemetry to avoid breaking console.error
      }
    };
  }
}

export const globalErrorHandler = new GlobalErrorHandlerService();
